import * as p from '@clack/prompts'
import type {
  BlobProvisionResult,
  RunVercelResult,
  VercelRunner
} from '@cli-types/next-adapter/vercel.js'
import { MAX_STORE_NAME_ATTEMPTS } from '@cli-constants/limits.js'
import { INTERACTIVE_PROVISION_TIMEOUT_MS, PROVISION_TIMEOUT_MS } from '@cli-constants/timeouts.js'
import { spinner } from '@cli-utils/terminal.js'
import pc from 'picocolors'
import { runVercel } from '../runner/run-vercel.js'
import { candidateStoreName } from './candidate-store-name.js'
import { deleteOrphanStore } from './delete-orphan-store.js'
import { isEnvVarConflict } from './is-env-var-conflict.js'
import { isNameTaken } from './is-name-taken.js'
import { pullBlobToken } from './pull-blob-token.js'

/**
 * The terminal rerun drops `--yes` so the user can answer Vercel's own prompts, and the quiet
 * version-bump search resumes after it: inherit mode captures no output, so a name conflict there
 * is invisible. Callers must stop any active spinner first — inherited stdio fights a clack spinner.
 */
export async function provisionBlobStoreInteractive(
  runner: VercelRunner,
  cwd: string,
  options: { name: string; env?: NodeJS.ProcessEnv }
): Promise<BlobProvisionResult> {
  const quietSpinner = spinner()
  quietSpinner.start('Creating your Vercel Blob store')

  let terminalFallbackRan = false
  let lastResult: RunVercelResult | undefined

  const pullToken = async (
    activeSpinner: ReturnType<typeof spinner>,
    storeName?: string
  ): Promise<BlobProvisionResult> => {
    const token = await pullBlobToken(runner, cwd, options.env)
    if (!token) {
      activeSpinner.stop('No BLOB_READ_WRITE_TOKEN came back from Vercel')
      return { ...(storeName && { storeName }), failure: 'env-pull-empty' }
    }
    activeSpinner.clear()
    return { token, ...(storeName && { storeName }) }
  }

  for (let attempt = 1; attempt <= MAX_STORE_NAME_ATTEMPTS; attempt++) {
    const storeName = candidateStoreName(options.name, attempt)
    const result = await runVercel(
      runner,
      ['blob', 'create-store', storeName, '--access', 'public', '--yes'],
      { cwd, mode: 'capture', timeoutMs: PROVISION_TIMEOUT_MS, env: options.env }
    )
    lastResult = result

    if (result.success) {
      quietSpinner.message('Retrieving the Blob read-write token')
      return pullToken(quietSpinner, storeName)
    }
    if (isEnvVarConflict(result)) {
      await deleteOrphanStore(runner, cwd, result, options.env)
      quietSpinner.message('Retrieving the Blob read-write token')
      return pullToken(quietSpinner)
    }
    if (isNameTaken(result)) continue

    if (terminalFallbackRan) break
    terminalFallbackRan = true

    // The rerun inherits stdio, which fights a running spinner.
    quietSpinner.clear()
    p.log.info(
      `Create your Blob store in the Vercel prompts below ${pc.dim('(connect it to all environments).')}`
    )

    const add = await runVercel(runner, ['blob', 'create-store', storeName, '--access', 'public'], {
      cwd,
      mode: 'inherit',
      timeoutMs: INTERACTIVE_PROVISION_TIMEOUT_MS,
      env: options.env
    })

    if (add.success) {
      const pullSpinner = spinner()
      pullSpinner.start('Retrieving the Blob read-write token')
      return pullToken(pullSpinner, storeName)
    }
    if (add.timedOut) return { failure: 'timeout' }
    lastResult = add

    quietSpinner.start('Creating your Vercel Blob store')
  }

  quietSpinner.stop('Vercel Blob provisioning did not complete')
  return { failure: lastResult?.timedOut ? 'timeout' : 'provision-failed' }
}
